import { Component, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { LucideAngularModule } from 'lucide-angular';
import { JourneyPhaseCardComponent } from './components/journey-phase-card/journey-phase-card.component';
import { JourneyService } from 'src/app/shared/services/journey.service';
import { JourneyPhase } from 'src/app/shared/models/journey.models';
import { JourneyHeaderComponent } from 'src/app/shared/components/journey-header/journey-header.component';
import { JourneyNextStepComponent } from 'src/app/shared/components/journey-next-step/journey-next-step.component';
import { NotificationService } from 'src/app/core/services/notification.service';

/**
 * Getting Started: the onboarding journey, and nothing else.
 *
 * The page reads top-down: where you stand (ring, level, milestone path), what to do next,
 * then the five phases as collapsible panels. The learning material the steps point at
 * (intro video, MOOC players, quiz, webinar cards, downloadable resources) lives on the
 * Resources tab — a step carrying such a target links there with `?block=<id>`.
 */
@Component({
  selector: 'app-help-onboarding',
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    LucideAngularModule,
    JourneyPhaseCardComponent,
    JourneyHeaderComponent,
    JourneyNextStepComponent,
  ],
  templateUrl: './help-onboarding.component.html',
})
export class HelpOnboardingComponent implements OnInit {
  private translocoService = inject(TranslocoService);
  private journeyService = inject(JourneyService);
  private notificationService = inject(NotificationService);

  journey = this.journeyService.journey;
  phases = this.journeyService.phases;
  isLoading = this.journeyService.isLoading;
  error = this.journeyService.error;

  /** Key of the step whose toggle is in flight, so its medal stays disabled. */
  pendingStepKey = signal<string | null>(null);

  /** Open panel. Starts on the phase in progress, then follows the reader. */
  expandedPhase = signal<JourneyPhase | null>(null);

  ngOnInit(): void {
    this.journeyService
      .loadIfNeeded()
      .subscribe(() =>
        this.expandedPhase.set(
          this.phases().find((phase) => phase.isCurrent)?.phase ?? null,
        ),
      );
  }

  togglePhase(phase: JourneyPhase): void {
    this.expandedPhase.update((current) => (current === phase ? null : phase));
  }

  /** Milestone clicked on the path: open that phase and bring it into view. */
  onPhaseSelected(phase: JourneyPhase): void {
    this.expandedPhase.set(phase);

    // Deferred so the panel is in the DOM before we scroll to it.
    setTimeout(() =>
      document
        .getElementById(`journey-phase-${phase}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
    );
  }

  onStepToggled(key: string): void {
    if (this.pendingStepKey() !== null) {
      return;
    }

    const levelBefore = this.journeyService.level();
    this.pendingStepKey.set(key);

    this.journeyService.toggleStep(key).subscribe({
      next: () => {
        this.pendingStepKey.set(null);
        this.celebrateLevelUp(levelBefore);
      },
      error: () => this.pendingStepKey.set(null),
    });
  }

  /** Announces the badge a step just earned — the payoff for finishing a whole phase. */
  private celebrateLevelUp(levelBefore: number): void {
    if (this.journeyService.level() <= levelBefore) {
      return;
    }

    const message = this.journeyService.isComplete()
      ? this.translocoService.translate(
          'help.onboarding.journey.journeyCompleted',
        )
      : this.translocoService.translate(
          'help.onboarding.journey.badgeUnlocked',
          {
            badge: this.translocoService.translate(
              this.journeyService.levelTitleKey(),
            ),
          },
        );

    this.notificationService.success(message);
  }
}
